/**
 * Modes:
 * - public     → meet.jit.si (demo, 5-min embed limit)
 * - selfhosted → your Docker Jitsi domain
 * - jaas       → 8x8 Jitsi as a Service (needs JWT API)
 */
export const JITSI_MODE = (process.env.REACT_APP_JITSI_MODE || "public").toLowerCase();

export const JITSI_DOMAIN =
  process.env.REACT_APP_JITSI_DOMAIN || "meet.jit.si";

export const JAAS_APP_ID = process.env.REACT_APP_JAAS_APP_ID || "";

export const JWT_API_URL =
  process.env.REACT_APP_JWT_API_URL || "http://localhost:4000/api/jitsi-token";

export const MEETING_SUBJECT =
  process.env.REACT_APP_MEETING_SUBJECT || "BIZ Consultancy Meeting";

export const JITSI_TOOLBAR_BUTTONS = [
  "microphone",
  "camera",
  "desktop",
  "recording", // near front so it stays visible (not only under ⋯)
  "chat",
  "raisehand",
  "participants-pane",
  "tileview",
  "fullscreen",
  "hangup",
  "livestreaming",
  "settings",
  "videoquality",
  "filmstrip",
  "invite",
  "feedback",
  "stats",
  "shortcuts",
  "help",
  "security",
  "select-background",
  "noisesuppression",
  "whiteboard",
  "sharedvideo",
  "shareaudio",
  "closedcaptions",
  "profile",
  "download",
  "embedmeeting",
];

export const getJitsiConfigOverwrite = () => ({
  // Skip Jitsi lobby / "Join meeting" screen — enter room immediately
  prejoinPageEnabled: false,
  prejoinConfig: {
    enabled: false,
  },
  disableDeepLinking: true,
  enableWelcomePage: false,
  enableClosePage: false,
  startWithAudioMuted: true,
  startWithVideoMuted: true,
  disableDesktopSharing: false,
  subject: MEETING_SUBJECT,
  defaultLanguage: "en",
  hideConferenceSubject: false,
  enableLobby: false,
  requireDisplayName: false,
  disableInviteFunctions: false,
  enableInsecureRoomNameWarning: false,
  // No Jitsi / third-party logo in the meeting UI
  defaultLogoUrl: "",
  logoImageUrl: "",
  hideLogo: true,
  // Server-side file recording (Jibri). Browser-local recording stays off.
  localRecording: {
    disable: true,
    notifyAllParticipants: true,
    disableSelfRecording: true,
  },
  fileRecordingsEnabled: true,
  recordingService: {
    enabled: true,
    sharingEnabled: false,
  },
  // Keep toolbar button list (newer Jitsi reads this, not only interfaceConfig)
  toolbarButtons: JITSI_TOOLBAR_BUTTONS,
  // Local / self-hosted: don't force mobile app download
  disableThirdPartyRequests: JITSI_MODE !== "public",
  // Do not play the phone dial tone or ringing sound when the room opens.
  disabledSounds: [
    "OUTGOING_CALL_START_SOUND",
    "OUTGOING_CALL_RINGING_SOUND",
    "OUTGOING_CALL_EXPIRED_SOUND",
    "OUTGOING_CALL_REJECTED_SOUND",
  ],
});

export const getJitsiInterfaceConfig = () => ({
  SHOW_JITSI_WATERMARK: false,
  SHOW_WATERMARK_FOR_GUESTS: false,
  SHOW_BRAND_WATERMARK: false,
  SHOW_POWERED_BY: false,
  JITSI_WATERMARK_LINK: "",
  BRAND_WATERMARK_LINK: "",
  DEFAULT_LOGO_URL: "",
  DEFAULT_WELCOME_PAGE_LOGO_URL: "",
  HIDE_DEEP_LINKING_LOGO: true,
  APP_NAME: "BIZ Consultancy Meeting",
  NATIVE_APP_NAME: "BIZ Consultancy Meeting",
  PROVIDER_NAME: "BIZ Consultancy",
  DEFAULT_BACKGROUND: "#07261D",
  MOBILE_APP_PROMO: false,
  DISABLE_RINGING: true,
  DISABLE_JOIN_LEAVE_NOTIFICATIONS: false,
  TOOLBAR_BUTTONS: JITSI_TOOLBAR_BUTTONS,
  SETTINGS_SECTIONS: [
    "devices",
    "language",
    "moderator",
    "profile",
    "calendar",
    "sounds",
    "more",
  ],
});

/**
 * Fetch JWT for JaaS from your backend.
 */
export async function fetchJaasToken({ roomName, participantName, moderator = true }) {
  const url = new URL(JWT_API_URL);
  url.searchParams.set("room", roomName);
  url.searchParams.set("name", participantName || "Guest");
  url.searchParams.set("moderator", moderator ? "true" : "false");

  const res = await fetch(url.toString());
  const data = await res.json();
  if (!res.ok) {
    throw new Error(data.error || "Failed to get JaaS token");
  }
  return data;
}
